import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Mic, MicOff, Square, Upload, Download, Trash2, Loader2,
  FileAudio, Save, Clock, FileText, Pause, Play, Link2,
  Radio, Sparkles, Zap, Activity, Waves
} from 'lucide-react';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return '00:00';
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const formatBytes = (b) => {
  if (!b && b !== 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

// Pseudo-animated waveform: 32 bars of varying heights
const WaveformBars = ({ active }) => (
  <div className="flex items-end gap-1 h-12 w-full max-w-md mx-auto" aria-hidden="true">
    {[...Array(32)].map((_, i) => (
      <span
        key={i}
        className={`flex-1 rounded-sm transition-all duration-300 ${active ? 'bg-gradient-to-t from-rose-500 via-fuchsia-400 to-cyan-300 animate-pulse' : 'bg-slate-700'}`}
        style={{
          animationDelay: active ? `${(i % 8) * 80}ms` : '0ms',
          height: active ? `${30 + ((i * 17) % 70)}%` : '20%',
        }}
      />
    ))}
  </div>
);

const CallsToText = () => {
  const { token } = useAuth();
  const authHeaders = { Authorization: `Bearer ${token}` };

  // Recorder state
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [recordingTitle, setRecordingTitle] = useState('');

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  // Server state
  const [transcripts, setTranscripts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [attachLeadId, setAttachLeadId] = useState('');
  const [loading, setLoading] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editingNotes, setEditingNotes] = useState('');
  const [editingTranscript, setEditingTranscript] = useState('');
  const [editingLeadId, setEditingLeadId] = useState('');

  // ---------- Server calls ----------
  const fetchTranscripts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${backendUrl}/api/calls-to-text/transcripts`, { headers: authHeaders });
      if (!r.ok) throw new Error('Failed to load transcripts');
      const data = await r.json();
      setTranscripts(data);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchLeads = useCallback(async () => {
    try {
      const r = await fetch(`${backendUrl}/api/nurture/leads`, { headers: authHeaders });
      if (!r.ok) return;
      const data = await r.json();
      setLeads(Array.isArray(data) ? data : (data.leads || []));
    } catch (e) {
      // silent
    }
  }, [token]);

  useEffect(() => { if (token) { fetchTranscripts(); fetchLeads(); } }, [token, fetchTranscripts, fetchLeads]);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (audioUrl) URL.revokeObjectURL(audioUrl);
  }, [audioUrl]);

  // ---------- Recorder controls ----------
  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        toast.error('Your browser does not support audio recording');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      streamRef.current = stream;
      setPermissionGranted(true);

      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime || 'audio/webm' });
        setAudioBlob(blob);
        if (audioUrl) URL.revokeObjectURL(audioUrl);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      };

      recorder.start(1000);
      setIsRecording(true);
      setIsPaused(false);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(prev => prev + 1), 1000);
      toast.success('Recording started');
    } catch (e) {
      toast.error(e.message || 'Microphone access denied');
    }
  };

  const pauseRecording = () => {
    const r = mediaRecorderRef.current;
    if (!r) return;
    if (r.state === 'recording') {
      r.pause();
      setIsPaused(true);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    } else if (r.state === 'paused') {
      r.resume();
      setIsPaused(false);
      timerRef.current = setInterval(() => setElapsed(prev => prev + 1), 1000);
    }
  };

  const stopRecording = () => {
    const r = mediaRecorderRef.current;
    if (r && r.state !== 'inactive') r.stop();
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setIsRecording(false);
    setIsPaused(false);
  };

  const discardRecording = () => {
    setAudioBlob(null);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl('');
    setRecordingTitle('');
    setElapsed(0);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('File exceeds 25MB Whisper limit'); return; }
    setAudioBlob(file);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(URL.createObjectURL(file));
    setRecordingTitle(file.name.replace(/\.[^.]+$/, ''));
    setElapsed(0);
  };

  const submitForTranscription = async () => {
    if (!audioBlob) { toast.error('No audio to transcribe'); return; }
    setTranscribing(true);
    try {
      const fd = new FormData();
      const ext = (audioBlob.type || '').includes('webm') ? 'webm' : ((audioBlob.name || '').split('.').pop() || 'webm');
      const filename = (audioBlob.name) ? audioBlob.name : `recording_${Date.now()}.${ext}`;
      fd.append('file', audioBlob, filename);
      if (recordingTitle.trim()) fd.append('title', recordingTitle.trim());
      if (attachLeadId) fd.append('lead_id', attachLeadId);
      if (elapsed > 0) fd.append('duration_seconds', String(elapsed));

      const r = await fetch(`${backendUrl}/api/calls-to-text/transcribe`, {
        method: 'POST',
        headers: authHeaders,
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Transcription failed');

      toast.success(attachLeadId ? 'Transcript ready & logged on lead' : 'Transcript ready');
      discardRecording();
      setAttachLeadId('');
      await fetchTranscripts();
      setSelected(data);
      setEditingNotes(data.notes || '');
      setEditingTranscript(data.transcript || '');
      setEditingLeadId(data.lead_id || '');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setTranscribing(false);
    }
  };

  const downloadTranscript = async (id) => {
    try {
      const r = await fetch(`${backendUrl}/api/calls-to-text/transcripts/${id}/download`, { headers: authHeaders });
      if (!r.ok) throw new Error('Download failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(transcripts.find(t => t.id === id)?.title || 'transcript').replace(/[^A-Za-z0-9_\- ]+/g, '_')}.txt`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const deleteTranscript = async (id) => {
    if (!window.confirm('Delete this transcript permanently?')) return;
    try {
      const r = await fetch(`${backendUrl}/api/calls-to-text/transcripts/${id}`, {
        method: 'DELETE', headers: authHeaders,
      });
      if (!r.ok) throw new Error('Delete failed');
      toast.success('Transcript deleted');
      if (selected?.id === id) setSelected(null);
      await fetchTranscripts();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const saveSelected = async () => {
    if (!selected) return;
    try {
      const r = await fetch(`${backendUrl}/api/calls-to-text/transcripts/${selected.id}`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          notes: editingNotes,
          transcript: editingTranscript,
          lead_id: editingLeadId || null,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Save failed');
      setSelected(data);
      await fetchTranscripts();
      toast.success('Saved');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const openTranscript = (t) => {
    setSelected(t);
    setEditingNotes(t.notes || '');
    setEditingTranscript(t.transcript || '');
    setEditingLeadId(t.lead_id || '');
  };

  return (
    <div className="min-h-screen flex flex-col bg-page text-text-main pb-24 md:pb-0 transition-colors" data-testid="calls-to-text-page">
      <Header />

      {/* Hero band with animated grid + glows */}
      <div className="relative overflow-hidden border-b border-slate-200 dark:border-slate-800/60">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-100 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950"></div>
        <div className="absolute inset-0 opacity-[0.06] dark:opacity-[0.06]" style={{
          backgroundImage: 'linear-gradient(rgba(34,211,238,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.6) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}></div>
        <div className="absolute -top-20 left-1/3 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 right-1/4 w-96 h-96 bg-fuchsia-500/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-cyan-500 via-blue-600 to-fuchsia-600 flex items-center justify-center shadow-2xl shadow-cyan-500/20">
              <Mic className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              <span className="absolute inset-0 rounded-2xl ring-2 ring-cyan-400/30 animate-pulse"></span>
            </div>
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 dark:border-cyan-500/20 text-cyan-700 dark:text-cyan-300 text-xs mb-2">
                <Sparkles className="w-3 h-3" /> AI-powered speech-to-text
              </div>
              <h1 className="text-2xl sm:text-4xl font-bold text-slate-900 dark:text-white tracking-tight">
                Calls <span className="bg-gradient-to-r from-cyan-500 via-fuchsia-500 to-cyan-500 dark:from-cyan-300 dark:via-fuchsia-300 dark:to-cyan-300 bg-clip-text text-transparent">To Text</span>
              </h1>
              <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mt-1 max-w-2xl">
                Speakerphone the call, hit record, and Whisper transcribes every word — auto-logged to the right lead.
              </p>
            </div>
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-6">
        {/* Recorder Console */}
        <div
          className="relative overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-800 bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-950 shadow-2xl"
          data-testid="recorder-card"
        >
          {/* glow */}
          <div className={`absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl pointer-events-none transition-opacity duration-700 ${isRecording && !isPaused ? 'bg-rose-500/30 opacity-80' : 'bg-cyan-500/15 opacity-60'}`}></div>
          <div className={`absolute -bottom-24 -left-16 w-72 h-72 rounded-full blur-3xl pointer-events-none transition-opacity duration-700 ${isRecording && !isPaused ? 'bg-fuchsia-500/30 opacity-70' : 'bg-blue-500/10 opacity-50'}`}></div>

          <div className="relative p-5 sm:p-8">
            <div className="flex items-center justify-between gap-3 mb-6">
              <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                <Activity className="w-4 h-4 text-cyan-500 dark:text-cyan-400" />
                <span>Recording Console</span>
              </div>
              {isRecording && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/15 border border-rose-500/40 text-rose-300 text-xs font-medium">
                  <span className={`w-2 h-2 rounded-full ${isPaused ? 'bg-amber-400' : 'bg-rose-400 animate-pulse'}`}></span>
                  {isPaused ? 'PAUSED' : 'LIVE'}
                </span>
              )}
            </div>

            {/* Big record button + waveform */}
            <div className="flex flex-col items-center text-center mb-6 sm:mb-8">
              <div className="relative mb-4">
                {!isRecording ? (
                  <button
                    onClick={startRecording}
                    disabled={!!audioBlob}
                    className="group relative w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-rose-500 via-rose-600 to-red-700 hover:from-rose-400 hover:to-red-600 disabled:from-slate-700 disabled:to-slate-800 disabled:cursor-not-allowed shadow-2xl shadow-rose-500/40 transition-all duration-300 hover:scale-105 active:scale-95"
                    data-testid="start-recording-btn"
                    aria-label="Start recording"
                  >
                    <span className={`absolute inset-0 rounded-full ring-4 ring-rose-400/30 ${audioBlob ? '' : 'animate-ping'}`}></span>
                    <Mic className="w-10 h-10 sm:w-12 sm:h-12 text-white mx-auto" />
                  </button>
                ) : (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={pauseRecording}
                      className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-slate-800 border-2 border-slate-600 hover:border-amber-400 hover:bg-slate-700 transition-all hover:scale-105 active:scale-95"
                      data-testid="pause-recording-btn"
                      aria-label={isPaused ? 'Resume' : 'Pause'}
                    >
                      {isPaused ? <Play className="w-7 h-7 sm:w-8 sm:h-8 text-amber-300 mx-auto" /> : <Pause className="w-7 h-7 sm:w-8 sm:h-8 text-amber-300 mx-auto" />}
                    </button>
                    <button
                      onClick={stopRecording}
                      className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-rose-500 via-rose-600 to-red-700 shadow-2xl shadow-rose-500/40 transition-all hover:scale-105 active:scale-95"
                      data-testid="stop-recording-btn"
                      aria-label="Stop recording"
                    >
                      {!isPaused && <span className="absolute inset-0 rounded-full ring-4 ring-rose-400/40 animate-ping"></span>}
                      <Square className="w-9 h-9 sm:w-10 sm:h-10 text-white mx-auto" fill="currentColor" />
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 text-3xl sm:text-4xl font-mono font-bold tracking-widest mb-3">
                <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-500 dark:text-cyan-400" />
                <span className={`bg-gradient-to-r ${isRecording && !isPaused ? 'from-rose-500 via-fuchsia-500 to-cyan-500 dark:from-rose-300 dark:via-fuchsia-300 dark:to-cyan-300' : 'from-cyan-500 to-blue-500 dark:from-cyan-300 dark:to-blue-300'} bg-clip-text text-transparent`} data-testid="elapsed-timer">
                  {formatDuration(elapsed)}
                </span>
              </div>

              <WaveformBars active={isRecording && !isPaused} />

              {!isRecording && !audioBlob && (
                <div className="flex flex-col sm:flex-row items-center gap-2 mt-4">
                  <span className="text-xs text-slate-500 dark:text-slate-500 inline-flex items-center gap-1">
                    <Waves className="w-3.5 h-3.5" />
                    Or upload an existing recording
                  </span>
                  <label className="inline-flex items-center cursor-pointer">
                    <input type="file" accept="audio/*" className="hidden" onChange={handleFileUpload} data-testid="upload-audio-input" />
                    <span className="inline-flex items-center px-3 py-1.5 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800/60 text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition">
                      <Upload className="w-3.5 h-3.5 mr-1" /> Upload audio
                    </span>
                  </label>
                </div>
              )}
            </div>

            {/* Audio preview / transcribe */}
            {audioBlob && !isRecording && (
              <div className="border-t border-slate-200 dark:border-slate-800/70 pt-5 space-y-4" data-testid="audio-preview">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    placeholder="Title (e.g. Discovery call – Jane Smith)"
                    value={recordingTitle}
                    onChange={(e) => setRecordingTitle(e.target.value)}
                    className="bg-white dark:bg-slate-800/70 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500"
                    data-testid="recording-title-input"
                  />
                  <span className="text-xs text-slate-500 dark:text-slate-400 self-center whitespace-nowrap">
                    <FileAudio className="w-3.5 h-3.5 inline mr-1" /> {formatBytes(audioBlob.size)}
                  </span>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                  <span className="text-xs text-slate-700 dark:text-slate-300 sm:w-32 flex items-center gap-1">
                    <Link2 className="w-3.5 h-3.5" /> Attach to lead
                  </span>
                  <select
                    className="flex-1 h-9 rounded-md border border-slate-300 dark:border-slate-700 px-2 text-sm bg-white dark:bg-slate-800/70 text-slate-900 dark:text-slate-100"
                    value={attachLeadId}
                    onChange={(e) => setAttachLeadId(e.target.value)}
                    data-testid="attach-lead-select"
                  >
                    <option value="">— No lead (standalone transcript) —</option>
                    {leads.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name} {l.lead_number ? `(${l.lead_number})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {audioUrl && <audio controls src={audioUrl} className="w-full" data-testid="audio-player" />}

                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={submitForTranscription}
                    disabled={transcribing}
                    className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white shadow-lg shadow-cyan-500/20"
                    data-testid="transcribe-btn"
                  >
                    {transcribing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
                    {transcribing ? 'Transcribing…' : 'Transcribe'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={discardRecording}
                    disabled={transcribing}
                    className="border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800/60 text-slate-700 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700"
                    data-testid="discard-recording-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Discard
                  </Button>
                </div>
              </div>
            )}

            {!permissionGranted && !isRecording && !audioBlob && (
              <p className="text-xs text-slate-500 dark:text-slate-500 text-center mt-4">
                <MicOff className="w-3.5 h-3.5 inline mr-1" />
                You'll be asked to allow microphone access on first use. Audio stays on your machine until you tap Transcribe.
              </p>
            )}
          </div>
        </div>

        {/* Selected transcript editor */}
        {selected && (
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 shadow-xl" data-testid="selected-transcript-card">
            <CardHeader className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div>
                <CardTitle className="text-base text-slate-900 dark:text-white flex items-center gap-2">
                  <Radio className="w-4 h-4 text-cyan-500 dark:text-cyan-400" />
                  {selected.title}
                </CardTitle>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {new Date(selected.created_at).toLocaleString()} • {selected.audio_format?.toUpperCase()} • {formatBytes(selected.file_size_bytes)}
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => downloadTranscript(selected.id)} className="border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800/60 text-slate-700 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700" data-testid="download-selected-btn">
                  <Download className="w-4 h-4 mr-1" /> .txt
                </Button>
                <Button variant="outline" size="sm" onClick={() => deleteTranscript(selected.id)} className="border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800/60 text-rose-500 dark:text-rose-300 hover:bg-slate-100 dark:hover:bg-slate-700" data-testid="delete-selected-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                  <Link2 className="w-3.5 h-3.5" /> Attached lead
                </label>
                <select
                  className="mt-1 w-full h-9 rounded-md border border-slate-300 dark:border-slate-700 px-2 text-sm bg-white dark:bg-slate-800/70 text-slate-900 dark:text-slate-100"
                  value={editingLeadId}
                  onChange={(e) => setEditingLeadId(e.target.value)}
                  data-testid="edit-lead-select"
                >
                  <option value="">— No lead —</option>
                  {leads.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name} {l.lead_number ? `(${l.lead_number})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Transcript</label>
                <Textarea
                  rows={10}
                  value={editingTranscript}
                  onChange={(e) => setEditingTranscript(e.target.value)}
                  className="bg-white dark:bg-slate-800/70 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
                  data-testid="edit-transcript-textarea"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Your notes</label>
                <Textarea
                  rows={4}
                  placeholder="Key takeaways, next actions, tone…"
                  value={editingNotes}
                  onChange={(e) => setEditingNotes(e.target.value)}
                  className="bg-white dark:bg-slate-800/70 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
                  data-testid="edit-notes-textarea"
                />
              </div>
              <div className="flex justify-end">
                <Button onClick={saveSelected} className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white" data-testid="save-transcript-btn">
                  <Save className="w-4 h-4 mr-1" /> Save changes
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* History */}
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100" data-testid="history-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-slate-900 dark:text-white">
              <FileText className="w-4 h-4 text-cyan-500 dark:text-cyan-400" /> Past Transcripts
              <Badge variant="secondary" className="ml-auto bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700">{transcripts.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-6 text-slate-400 dark:text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : transcripts.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-500 text-center py-6">No transcripts yet. Hit the big red button to get started.</p>
            ) : (
              <div className="divide-y divide-slate-200 dark:divide-slate-800">
                {transcripts.map((t) => (
                  <div
                    key={t.id}
                    className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 px-2 rounded transition ${selected?.id === t.id ? 'bg-cyan-500/10 border border-cyan-500/20' : ''}`}
                    onClick={() => openTranscript(t)}
                    data-testid={`transcript-row-${t.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{t.title}</p>
                        {t.lead_name && (
                          <Badge variant="outline" className="text-xs border-cyan-500/40 dark:border-cyan-500/30 text-cyan-700 dark:text-cyan-300 bg-cyan-500/10" data-testid={`lead-chip-${t.id}`}>
                            <Link2 className="w-3 h-3 mr-1" /> {t.lead_name}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-500 truncate">
                        {new Date(t.created_at).toLocaleString()} • {(t.transcript || '').slice(0, 80)}{(t.transcript || '').length > 80 ? '…' : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 self-end sm:self-center">
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); downloadTranscript(t.id); }} className="hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300" data-testid={`download-${t.id}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); deleteTranscript(t.id); }} className="hover:bg-slate-100 dark:hover:bg-slate-800" data-testid={`delete-${t.id}`}>
                        <Trash2 className="w-4 h-4 text-rose-500 dark:text-rose-400" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default CallsToText;
